import { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown, X, Check } from "lucide-react";
import styles from "./SelectDropdown.module.css";

const normalizeOptions = (rawOptions = []) => {
  if (!Array.isArray(rawOptions)) return [];
  return rawOptions
    .filter(Boolean)
    .map((opt) => {
      if (typeof opt === "string" || typeof opt === "number") {
        return { value: opt, label: String(opt) };
      }
      const val = opt.value !== undefined ? opt.value : (opt.id !== undefined ? opt.id : "");
      return {
        value: val,
        label: opt.label || opt.name || String(val ?? ""),
        description: opt.description,
        icon: opt.icon,
        disabled: Boolean(opt.disabled),
      };
    });
};

const SelectDropdown = ({
  options = [],
  value,
  onChange,
  name,
  id,
  placeholder = "Select an option...",
  isMulti = false,
  isSearchable = true,
  isClearable = true,
  disabled = false,
  error = false,
  emptyMessage = "No options found",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when opened and calculate flip direction
  useEffect(() => {
    if (isOpen) {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        if (spaceBelow < 280 && spaceAbove > spaceBelow) {
          setOpenUpward(true);
        } else {
          setOpenUpward(false);
        }
      }
      if (isSearchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }, [isOpen, isSearchable]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return normalizedOptions;
    const term = searchTerm.toLowerCase();
    return normalizedOptions.filter(
      (opt) =>
        (opt.label && opt.label.toLowerCase().includes(term)) ||
        (opt.description && opt.description.toLowerCase().includes(term))
    );
  }, [normalizedOptions, searchTerm]);

  const selectedOptions = useMemo(() => {
    if (isMulti) {
      const arr = Array.isArray(value) ? value : [];
      return normalizedOptions.filter((opt) => arr.includes(opt.value));
    }
    return normalizedOptions.find((opt) => opt.value === value) || null;
  }, [normalizedOptions, value, isMulti]);

  const handleSelect = (opt) => {
    if (opt.disabled) return;

    if (isMulti) {
      const currentArr = Array.isArray(value) ? [...value] : [];
      const existsIndex = currentArr.indexOf(opt.value);
      let nextArr;
      if (existsIndex > -1) {
        nextArr = currentArr.filter((v) => v !== opt.value);
      } else {
        nextArr = [...currentArr, opt.value];
      }
      if (onChange) {
        const syntheticEvent = {
          target: { name: name || "", value: nextArr },
          currentTarget: { name: name || "", value: nextArr },
          value: nextArr,
        };
        onChange(syntheticEvent, nextArr);
      }
    } else {
      if (onChange) {
        const nextVal = opt.value !== undefined ? opt.value : "";
        const syntheticEvent = {
          target: { name: name || "", value: nextVal },
          currentTarget: { name: name || "", value: nextVal },
          value: nextVal,
        };
        onChange(syntheticEvent, nextVal);
      }
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) {
      const clearVal = isMulti ? [] : "";
      const syntheticEvent = {
        target: { name: name || "", value: clearVal },
        currentTarget: { name: name || "", value: clearVal },
        value: clearVal,
      };
      onChange(syntheticEvent, clearVal);
    }
  };

  const handleRemoveTag = (e, valToRemove) => {
    e.stopPropagation();
    if (isMulti && onChange) {
      const currentArr = Array.isArray(value) ? [...value] : [];
      const nextArr = currentArr.filter((v) => v !== valToRemove);
      const syntheticEvent = {
        target: { name: name || "", value: nextArr },
        currentTarget: { name: name || "", value: nextArr },
        value: nextArr,
      };
      onChange(syntheticEvent, nextArr);
    }
  };

  const triggerClass = [
    styles.trigger,
    isOpen ? styles.isOpen : "",
    error ? styles.hasError : "",
    disabled ? styles.disabled : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const hasSelection = isMulti
    ? selectedOptions && selectedOptions.length > 0
    : selectedOptions !== null;

  return (
    <div ref={containerRef} className={`${styles.container} ${isOpen ? styles.containerOpen : ""}`}>
      <div
        id={id || name}
        className={triggerClass}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
      >
        <div className={styles.valuesArea}>
          {!hasSelection ? (
            <span className={styles.placeholder}>{placeholder}</span>
          ) : isMulti ? (
            selectedOptions.map((opt) => (
              <span key={opt.value} className={styles.pillTag}>
                {opt.icon && <span>{opt.icon}</span>}
                <span>{opt.label}</span>
                {!disabled && (
                  <button
                    type="button"
                    className={styles.pillRemoveBtn}
                    onClick={(e) => handleRemoveTag(e, opt.value)}
                    aria-label={`Remove ${opt.label}`}
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            ))
          ) : (
            <div className={styles.singleValue}>
              {selectedOptions?.icon && <span>{selectedOptions.icon}</span>}
              <span>{selectedOptions?.label || ""}</span>
            </div>
          )}
        </div>

        <div className={styles.controlsArea}>
          {isClearable && hasSelection && !disabled && (
            <button
              type="button"
              className={styles.clearBtn}
              onClick={handleClear}
              tabIndex={-1}
              aria-label="Clear selection"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={16}
            className={`${styles.chevronIcon} ${isOpen ? styles.rotated : ""}`}
          />
        </div>
      </div>

      {isOpen && !disabled && (
        <div className={`${styles.dropdownMenu} ${openUpward ? styles.openUpward : ""}`}>
          {isSearchable && (
            <div className={styles.searchBox}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className={styles.optionsList}>
            {filteredOptions.length === 0 ? (
              <div className={styles.emptyState}>{emptyMessage}</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = isMulti
                  ? Array.isArray(value) && value.includes(opt.value)
                  : value === opt.value;

                return (
                  <div
                    key={opt.value}
                    className={`${styles.optionItem} ${
                      isSelected ? styles.isSelected : ""
                    }`}
                    onClick={() => handleSelect(opt)}
                  >
                    <div className={styles.optionLabel}>
                      {opt.icon && <span>{opt.icon}</span>}
                      <div>
                        <div>{opt.label}</div>
                        {opt.description && (
                          <div className={styles.optionDesc}>
                            {opt.description}
                          </div>
                        )}
                      </div>
                    </div>
                    {isSelected && <Check size={14} />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectDropdown;
